import React, { createContext, useMemo, useState, useEffect } from 'react'
import { useQuery } from '@apollo/client/react'
import queryUniqueBosses from 'graphql/queries/queryUniqueBosses'
import { getLocalStorageToken } from 'store/localStorage'
import { useCustomerSettings } from 'hooks/useCustomerSettings'
import { useOrganisations } from 'hooks/useOrganisations'

export interface Boss {
  bossId: number
  firstName: string | null
  surName: string | null
  count: number
}

interface BossFilterContextValue {
  bosses: Boss[]
  selectedBossId: number | undefined
  setSelectedBossId: (bossId: number | undefined) => void
  myIpPersonId: number | null
  loading: boolean
  enabled: boolean
}

export const BossFilterContext = createContext<BossFilterContextValue | null>(null)

const STORAGE_KEY = 'bossFilterSelectedId'

export const BossFilterProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { bossFilterDefault, loading: settingsLoading } = useCustomerSettings()
  const { excludeOrganisationIds } = useOrganisations()
  const enabled = bossFilterDefault === 'all' || bossFilterDefault === 'own'

  const { data, loading: bossesLoading } = useQuery<{
    viewer: {
      id: number
      ipPersonId: number | null
      uniqueBosses: Boss[]
    } | null
  }>(queryUniqueBosses, {
    variables: { token: getLocalStorageToken(), excludeOrganisationIds },
    fetchPolicy: 'cache-first',
    skip: !enabled,
  })

  const loading = settingsLoading || bossesLoading
  const bosses = data?.viewer?.uniqueBosses ?? []
  const myIpPersonId = data?.viewer?.ipPersonId ?? null

  const [selectedBossId, setSelectedBossIdState] = useState<number | undefined>(() => {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === 'all') return undefined
    if (stored) return Number(stored)
    return undefined
  })
  const [initialized, setInitialized] = useState(false)

  // Apply default on first load, validate stored value against available bosses
  useEffect(() => {
    if (initialized || loading || !enabled || bosses.length === 0) return
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored !== null && stored !== 'all') {
      // Validate stored bossId still exists in available bosses
      const storedId = Number(stored)
      if (!bosses.some((b) => b.bossId === storedId)) {
        // Stale value — reset to Alla
        setSelectedBossIdState(undefined)
        window.localStorage.setItem(STORAGE_KEY, 'all')
      }
      setInitialized(true)
      return
    }
    if (stored !== null) {
      setInitialized(true)
      return
    }
    // No saved preference — apply customer default
    if (bossFilterDefault === 'own' && myIpPersonId && bosses.some((b) => b.bossId === myIpPersonId)) {
      setSelectedBossIdState(myIpPersonId)
      window.localStorage.setItem(STORAGE_KEY, String(myIpPersonId))
    }
    setInitialized(true)
  }, [loading, enabled, bosses, myIpPersonId, bossFilterDefault, initialized])

  const setSelectedBossId = (bossId: number | undefined) => {
    setSelectedBossIdState(bossId)
    if (bossId === undefined) {
      window.localStorage.setItem(STORAGE_KEY, 'all')
    } else {
      window.localStorage.setItem(STORAGE_KEY, String(bossId))
    }
  }

  const value = useMemo(
    () => ({ bosses, selectedBossId, setSelectedBossId, myIpPersonId, loading, enabled }),
    [bosses, selectedBossId, myIpPersonId, loading, enabled],
  )

  return (
    <BossFilterContext.Provider value={value}>
      {children}
    </BossFilterContext.Provider>
  )
}
